/**
 * TODO: FINISH THIS DRAFT
 * 1. Pass the order - for redirect to the order page | OR | disable order cleanup and reuse the last order ID
 * 2. Persist the paymentSlice state in the LocalStorage
 */
import type { SolidgatePaymentPriceBreakdown } from '@/types/payment';

import { ExtLink } from '@/components/common/link/ExtLink';
import { IconSvg } from '@/components/common/icons/IconSvg';
import { Modal } from '@/components/common/Modal';
import { SOLIDGATE_PAYMENT_CARDS } from '@feature/payment/solidgate/config/solidgate.form.config';
import { SolidgatePaymentForm } from '@feature/payment/solidgate/form/SolidgatePaymentForm';
import { Tooltip, Spin } from 'antd';
import { checkHasResign } from '@feature/payment/solidgate/hooks/useSolidgateConditions';
import { resetPaymentDetails, selectAllPaymentState } from '@/store/payment/paymentSlice';
import { startCase } from 'lodash';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useState, useEffect } from 'react';

// Styles
import cn from 'classnames';
import s from './SolidgatePaymentModal.module.less';

/**
 * SolidgatePaymentModal renders the payment form for Solidgate payment method.
 * This component mounts only when the merchantData & order set in the Redux store (paymentSlice).
 * Modal automatically closes when the payment is cancelled or completed.
 * @see SolidgatePaymentForm - this component contains the payment form and handles the payment process.
 * @see SolidgatePaymentSuccessModal - this modal is shown after the payment form is submitted successfully.
 */
export const SolidgatePaymentModal: FC = () => {
  const dispatch = useAppDispatch();

  // Price breakdown state received after payment form is mounted and event onPaymentDetails is triggered
  const [priceBreakdown, setPriceBreakdown] = useState<SolidgatePaymentPriceBreakdown | null>(null);

  const [isMounted, setMounted] = useState(false);

  const { order, paymentDetails } = useAppSelector(selectAllPaymentState);

  const [isCreditCardMode, setCreditCardMode] = useState<boolean>(false);

  /**
   * If the paymentDetails has resign data,
   * set credit card mode to true to display 2nd view/state of payment form
   */
  useEffect(() => {
    console.log('[payment modal] Resign:', checkHasResign(paymentDetails));
    if (!checkHasResign(paymentDetails)) return;
    setCreditCardMode(true);
  }, [paymentDetails]);

  // Check if all required data is available to display the payment modal
  if (!paymentDetails || !order) return null;

  // Close modal and reset isMounted state
  const handleCloseModal = () => {
    console.log('[payment modal] Payment cancelled');
    setMounted(false);
    dispatch(resetPaymentDetails());
  };

  // Get price details from payment form
  const { productPrice } = priceBreakdown || {};

  return (
    <>
      {!isMounted && (
        // Show loading spinner while the payment form is being mounted
        <Modal width="100%" visible closable={false} className={s.mask}>
          <Spin size="large" tip="Loading payment form" />
        </Modal>
      )}
      {/**
       * Display modal only when component SolidgatePaymentForm is mounted
       *  -> prevents interface blinking (requires forceRender)
       */}
      <Modal
        className={cn(s.modal, { [s.creditCardMode]: isCreditCardMode })}
        open={isMounted}
        onCancel={handleCloseModal}
        forceRender
        width="initial"
      >
        <div className={s.container}>
          <div className={s.formWrapper}>
            {productPrice && (
              <span className={s.price}>
                Amount:{' '}
                <strong className={s.priceValue}>
                  {productPrice.amount} {productPrice.currency}
                </strong>
              </span>
            )}
            <SolidgatePaymentForm
              creditCardMode={isCreditCardMode}
              onCreditCardModeChange={setCreditCardMode}
              paymentDetails={paymentDetails}
              onReady={setMounted}
              onPaymentDetails={setPriceBreakdown}
              order={order}
            />
          </div>
          <div className={s.infoBanner}>
            {isCreditCardMode ? (
              <>
                <img className={s.infoImage} src="/img/svg/payment-banner-card.svg" alt="card details" />
                <h3 className={s.infoTitle}>Enter Card Details</h3>
                <div className={s.infoDescription}>
                  To continue with this transaction, please provide your card details. Please ensure that your card has a valid expiry date
                  and sufficient funds to finalize this transaction.
                  <hr />
                  <IconSvg icon="IcoSCircleInfo" />
                  Some transactions may require up to 15 minutes to process.
                </div>
              </>
            ) : (
              <>
                <img className={s.infoImage} src="/img/svg/payment-banner-shield.svg" alt="secure payment" />
                <h3 className={s.infoTitle}>Your Payment is Secure</h3>
                <div className={s.infoDescription}>
                  We use bank-level security (256-bit SSL encryption) to protect your personal and payment information, ensuring every
                  transaction is 100% safe.
                </div>
              </>
            )}
            <div className={s.infoCards}>
              {SOLIDGATE_PAYMENT_CARDS.map((card) => (
                <Tooltip key={card} title={startCase(`Pay with ${card} card`)}>
                  <img alt={card} src={`/img/svg/pay-${card}.svg`} />
                </Tooltip>
              ))}
            </div>
          </div>
          <footer className={s.policy}>
            <IconSvg icon="IcoSCircleInfo" />
            <p>
              By completing this purchase, you agree to our <ExtLink href="/terms-and-conditions">Terms and Conditions</ExtLink>,{' '}
              <ExtLink href="/privacy-policy">Privacy Policy</ExtLink>, <ExtLink href="/money-back-guarantee">Money Back Guarantee</ExtLink>
              , <ExtLink href="/fair-use-policy">Fair Use Policy</ExtLink>, and{' '}
              <ExtLink href="/plagiarism-and-ai-policy">Plagiarism & AI Policy</ExtLink>
            </p>
          </footer>
        </div>
      </Modal>
    </>
  );
};
